import React from "react";
import { Box, Badge, Link, SpaceBetween } from "@amzn/awsui-components-react";

/**
 * A single course card inside a curriculum cell. Shows the title, provider +
 * duration, and status badges (coming soon / mandatory / ETA). When a URL is
 * present the title links out to the training; otherwise it renders as plain
 * text (e.g. while a course is still coming soon).
 */
// A course is "New" for 30 days after it transitioned into AVAILABLE. The
// backend stamps `available_since` on that transition; NULL means never live.
const NEW_WINDOW_DAYS = 30;
const isNew = (course) => {
  if (course.status !== "AVAILABLE" || !course.available_since) return false;
  const since = new Date(course.available_since);
  if (Number.isNaN(since.getTime())) return false;
  const ageMs = Date.now() - since.getTime();
  return ageMs >= 0 && ageMs <= NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000;
};

const CourseCard = ({ course, tierCode }) => {
  if (!course) return null;

  const providerLine = [course.provider_name, course.duration_label]
    .filter(Boolean)
    .join(" · ");

  const comingSoon = course.status === "COMING_SOON";
  const showNew = isNew(course);

  const titleNode =
    course.url && !comingSoon ? (
      <Link href={course.url} external fontSize="body-m">
        {course.title}
      </Link>
    ) : (
      <Box variant="strong">{course.title}</Box>
    );

  // Tint the card to its tier (matches the prototype's purple/teal/pink cells).
  const tierClass = tierCode ? ` raise-tier-${tierCode.toLowerCase()}` : "";

  return (
    <Box padding="s" className={`raise-course-card${tierClass}`}>
      <SpaceBetween size="xxs">
        {titleNode}
        {providerLine ? (
          <Box color="text-body-secondary" fontSize="body-s">
            {providerLine}
          </Box>
        ) : null}
        <SpaceBetween direction="horizontal" size="xxs">
          {showNew ? <Badge color="green">New</Badge> : null}
          {course.is_mandatory ? <Badge color="red">mandatory</Badge> : null}
          {comingSoon ? <Badge color="grey">coming soon</Badge> : null}
          {course.eta_date ? (
            <Badge color="blue">{`ETA ${course.eta_date}`}</Badge>
          ) : null}
        </SpaceBetween>
      </SpaceBetween>
    </Box>
  );
};

export default CourseCard;
